import type { FC } from 'react'

import { Icon, StyledText } from '@skytech/ui'
import CheckCircleOutlineIcon from 'mdi-react/CheckCircleOutlineIcon'
import { twJoin } from 'tailwind-merge'

import { useAppContext } from '@/App.context'
import { useFormattedNumberWithCurrency } from '@/hooks/use-formatted-number-with-currency'

import { Translation } from '../i18n/Translation'
import { statusStyleByWhiteLabel } from '../util/style-constant'

export const CartTotalSummary: FC<{
  totalPayrollDeduction: number
  grossSalary: { monthlyPrice: number; paymentInterval: number }[]
  interval?: number | string
  totalDeductionByInterval: { monthlyPrice: number; paymentInterval: number }[]
}> = ({ totalPayrollDeduction, grossSalary, interval, totalDeductionByInterval }) => {
  const { whiteLabel } = useAppContext()
  const { formatNumber } = useFormattedNumberWithCurrency()

  if (totalPayrollDeduction === 0 && grossSalary.length === 0) {
    return (
      <div>
        <StyledText colorMode="black" className="!leading-none">
          <Icon colorMode="success" size="small" className="mr-1" isWhiteLabeled>
            <CheckCircleOutlineIcon />
          </Icon>
          <Translation id="order completely covered by employer" />
        </StyledText>
      </div>
    )
  }

  return (
    <>
      {totalPayrollDeduction > 0 && interval === 'default' && (
        <DefaultSalaryDeductionTotalSummary totalPayrollDeduction={totalPayrollDeduction} />
      )}

      {totalPayrollDeduction > 0 && interval === 'varied' && (
        <VariedSalaryDeductionTotalSummary totalByInterval={totalDeductionByInterval} />
      )}

      {totalPayrollDeduction > 0 && typeof interval === 'number' && (
        <SameIntervalSalaryDeductionTotalSummary
          total={totalPayrollDeduction}
          totalByInterval={totalDeductionByInterval[0]}
        />
      )}

      {/* Gross Salary */}
      {grossSalary.length > 0 && (
        <>
          {grossSalary.map(({ monthlyPrice, paymentInterval }) => (
            <div
              key={paymentInterval}
              className="mt-2 flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:gap-1.5"
            >
              <StyledText size="large" className="!leading-none">
                <span
                  className={twJoin(
                    statusStyleByWhiteLabel[whiteLabel]?.warning.text ||
                      statusStyleByWhiteLabel.skytech!.warning.text
                  )}
                >
                  {formatNumber({ amount: monthlyPrice })}
                </span>
              </StyledText>
              {paymentInterval <= 1 ? (
                <StyledText
                  colorMode="black"
                  size="regular-plus"
                  className="max-mobile:!text-sm !leading-none"
                >
                  <Translation id="via payroll deduction" />
                </StyledText>
              ) : (
                <StyledText
                  colorMode="black"
                  size="regular-plus"
                  className="max-mobile:!text-sm !leading-none"
                >
                  <Translation
                    id="per month via payroll deduction ({paymentInterval} {paymentInterval, plural, one {month} other {months}})"
                    paymentInterval={paymentInterval}
                  />
                </StyledText>
              )}
            </div>
          ))}
        </>
      )}
    </>
  )
}

const DefaultSalaryDeductionTotalSummary: FC<{ totalPayrollDeduction: number }> = ({
  totalPayrollDeduction,
}) => {
  const { whiteLabel } = useAppContext()
  const { formatNumber } = useFormattedNumberWithCurrency()
  return (
    <div className="mt-2 flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:gap-1.5">
      <StyledText size="xl" className="!leading-none">
        <span
          className={twJoin(
            statusStyleByWhiteLabel[whiteLabel]?.warning.text ||
              statusStyleByWhiteLabel.skytech!.warning.text
          )}
        >
          {formatNumber({ amount: totalPayrollDeduction })}
        </span>
      </StyledText>{' '}
      <StyledText
        colorMode="black"
        size="regular-plus"
        className="max-mobile:!text-sm !leading-none"
      >
        <Translation id="sent for payroll deduction" />
      </StyledText>
    </div>
  )
}

const VariedSalaryDeductionTotalSummary: FC<{
  totalByInterval: { monthlyPrice: number; paymentInterval: number }[]
}> = ({ totalByInterval }) => {
  const { whiteLabel } = useAppContext()
  const { formatNumber } = useFormattedNumberWithCurrency()
  const totalSalaryDeduction = totalByInterval.filter(item => item.monthlyPrice > 0)
  return (
    <>
      {totalSalaryDeduction.map(({ monthlyPrice, paymentInterval }) => (
        <div
          key={paymentInterval}
          className="mt-2 flex flex-row flex-wrap items-baseline gap-1 sm:gap-1.5"
        >
          <StyledText size="large" className="!leading-none">
            <span
              className={twJoin(
                statusStyleByWhiteLabel[whiteLabel]?.warning.text ||
                  statusStyleByWhiteLabel.skytech!.warning.text
              )}
            >
              {formatNumber({ amount: monthlyPrice })}
            </span>
          </StyledText>
          {paymentInterval <= 1 ? (
            <StyledText
              colorMode="black"
              size="regular-plus"
              className="max-mobile:!text-sm !leading-none"
            >
              <Translation id="next month" />
            </StyledText>
          ) : (
            <StyledText
              colorMode="black"
              size="regular-plus"
              className="max-mobile:!text-sm !leading-none"
            >
              <Translation
                id="in next {paymentInterval} {paymentInterval, plural, one {month} other {months}}"
                paymentInterval={paymentInterval}
              />
            </StyledText>
          )}
        </div>
      ))}
    </>
  )
}

const SameIntervalSalaryDeductionTotalSummary: FC<{
  total: number
  totalByInterval: { monthlyPrice: number; paymentInterval: number }
}> = ({ total, totalByInterval }) => {
  const { whiteLabel } = useAppContext()
  const { formatNumber } = useFormattedNumberWithCurrency()
  return (
    <div className="mt-2 flex flex-col flex-wrap gap-0.5 sm:flex-row sm:items-baseline sm:gap-1.5">
      <StyledText size="xl" className="!leading-none">
        <span
          className={twJoin(
            statusStyleByWhiteLabel[whiteLabel]?.warning.text ||
              statusStyleByWhiteLabel.skytech!.warning.text
          )}
        >
          {formatNumber({ amount: total })}
        </span>
      </StyledText>{' '}
      <StyledText
        colorMode="black"
        size="regular-plus"
        className="max-mobile:!text-sm !leading-none"
      >
        <Translation
          id="salary deduction will be split into"
          payment={formatNumber({ amount: total })}
          paymentInterval={totalByInterval.paymentInterval}
        />
      </StyledText>
      <StyledText size="large" className="mt-2 !leading-none sm:mt-0">
        <span
          className={twJoin(
            statusStyleByWhiteLabel[whiteLabel]?.warning.text ||
              statusStyleByWhiteLabel.skytech!.warning.text
          )}
        >
          {formatNumber({ amount: totalByInterval.monthlyPrice })}
        </span>
      </StyledText>{' '}
      <StyledText
        colorMode="black"
        size="regular-plus"
        className="max-mobile:!text-sm !leading-none"
      >
        <Translation
          id="for the next {paymentInterval} {paymentInterval, plural, one {month} other {months}}"
          payment={formatNumber({ amount: total })}
          paymentInterval={totalByInterval.paymentInterval}
        />
      </StyledText>
    </div>
  )
}
